import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom-v5-compat";
import { round } from "lodash";

import api from "apiSingleton";
import { AuthContext, JobsContext } from "context/providers";
import { CURRENCY_CONVERSION_CONFIG } from "lookup";

import AppDialog from "components/AppDialog";
import Input from "components/base/Input";
import {
  FORM_INPUT_CLASSNAME,
  FORM_LABEL_CLASSNAME,
} from "components/base/FormLabel";
import {
  baseToTargetCurrency,
  loggedUserShouldINRConvert,
} from "utils/helpers/users";

const BODY_TEXT_CLASSNAME = "font-inter text-base font-normal text-slate-700";

const getStandardMarkup = (desiredRate) => round(desiredRate * 1.5, 0);

// Display our Standard Markup
const StandardMarkup = ({ desiredRate }) => {
  if (!desiredRate) {
    return null;
  }

  return (
    <span className="whitespace-nowrap font-bold">
      {"$" + getStandardMarkup(desiredRate)}{" "}
      <span title="1.5 times the desired rate of freelancer">(1.5x)</span>
    </span>
  );
};

//Compute Calculated Markup based on current inputRate
const CalculatedMarkup = ({ desiredRate, currentRate }) => {
  if (!desiredRate) {
    return null;
  }

  const markup = ((currentRate - desiredRate) / currentRate) * 100;
  let markupPercent = round(markup, 2);

  if (!isFinite(markupPercent)) {
    markupPercent = 0;
  }

  return (
    <span
      className={
        markupPercent > 0
          ? "font-bold text-green-500"
          : "font-bold text-red-500"
      }
    >
      {markupPercent.toFixed(2)}
      {"%"}
    </span>
  );
};

const CustomerRateModal = ({ match, settingRate, setSettingRate }) => {
  const { initJob } = useContext(JobsContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const desiredRate = match?.rate?.value;

  const savedRate = match?.customerRate?.value;

  // Show the rate already stored on the match. Only fall back to our standard
  // markup when no rate has been set yet.
  const initialInputNum = savedRate || getStandardMarkup(desiredRate ?? 0);

  const shouldConvert = loggedUserShouldINRConvert(loggedInUser);

  const initialDisplayNum = shouldConvert
    ? baseToTargetCurrency(
        initialInputNum || 0,
        CURRENCY_CONVERSION_CONFIG.exchangeFactor,
        false
      )
    : initialInputNum;

  const params = useParams();

  const [inputNum, setInputNum] = useState(initialDisplayNum);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const usdValueRef = useRef(Number(initialInputNum) || 0);

  // The modal stays mounted while closed, so re-sync the input with the match
  // every time it is opened and after a rate is saved.
  useEffect(() => {
    if (!settingRate) {
      return;
    }

    setInputNum(initialDisplayNum);
    usdValueRef.current = initialInputNum;
  }, [settingRate, initialDisplayNum, initialInputNum]);

  const setRate = async (addRate) => {
    try {
      setLoading(true);

      const attrs = {};

      const rate = {
        value: addRate ? usdValueRef.current : 0,
        currency: "USD",
      };

      attrs.customerRate = rate;
      attrs.status = match?.status;

      await api.match.update({
        input: {
          applicationId: match?.applicationId,
          jobOpportunityId: match?.jobOpportunityId,
          ...attrs,
        },
      });

      await initJob(params.id);

      setSettingRate(false);
      setLoading(false);
    } catch (e) {
      console.error("setRate error: ", e);

      setLoading(false);
      setErr(true);
    }
  };

  const title = match?.customerRate?.value
    ? "Edit Customer Rate"
    : "Customer Rate";

  const handleClose = () => {
    setSettingRate(false);
    setErr(false);
  };

  return (
    <AppDialog
      open={settingRate}
      onClose={handleClose}
      header={title}
      className="w-full max-w-[360px] overflow-hidden rounded-lg"
      contentClassName="px-6 pb-6 pt-4"
    >
      <div className="flex h-full flex-col justify-between">
        <div className="flex flex-col">
          <Input
            label="Rate"
            labelClassName={FORM_LABEL_CLASSNAME}
            inputClassName={FORM_INPUT_CLASSNAME}
            type="number"
            value={inputNum}
            min="0"
            onChange={({ value }) => {
              if (shouldConvert) {
                const newConvertedValue =
                  CURRENCY_CONVERSION_CONFIG.exchangeFactor *
                  (Number(value) || 0);

                usdValueRef.current = parseInt(
                  Math.ceil(Number(newConvertedValue.toFixed(2)))
                );
              } else {
                usdValueRef.current = Number(value) || 0;
              }

              setInputNum(value);
            }}
          />
          {shouldConvert && (
            <span className={`-mt-2 mb-2 ${BODY_TEXT_CLASSNAME}`}>
              = {usdValueRef.current} (USD)
            </span>
          )}
          <div className={`mt-1 space-y-1 ${BODY_TEXT_CLASSNAME}`}>
            <p>
              <span>Desired Rate: </span>
              <span className="font-bold">${desiredRate || "0"}</span>
              {shouldConvert &&
                ` USD (${baseToTargetCurrency(
                  desiredRate || 0,
                  CURRENCY_CONVERSION_CONFIG.exchangeFactor
                )} INR)`}
            </p>
            <p>
              <span>Standard Rate w/ Markup: </span>
              <StandardMarkup desiredRate={desiredRate} />
            </p>
            <p>
              <span>Calculated Markup: </span>
              <CalculatedMarkup
                desiredRate={desiredRate}
                currentRate={usdValueRef.current}
              />
            </p>
          </div>
        </div>
        <div className="mt-4 flex w-full justify-end gap-2.5">
          <button
            type="button"
            className="rounded-md bg-sky-500 px-4 py-3 font-inter text-sm font-medium text-white transition hover:bg-sky-600 disabled:opacity-50"
            onClick={() => setRate(true)}
            disabled={loading}
          >
            {loading ? "setting rate..." : "Set Rate"}
          </button>
          <button
            type="button"
            className="rounded-md bg-red-600 px-4 py-3 font-inter text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
            onClick={() => setRate(false)}
            disabled={loading}
          >
            Void Rate
          </button>
        </div>
        {err && (
          <div className="text-red-400">
            Something went wrong, please try again
          </div>
        )}
      </div>
    </AppDialog>
  );
};

export default CustomerRateModal;
